import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PaperPlaneTilt, Brain, Lightning } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChatDrawer({ open, onOpenChange, page }) {
  const { user } = useAuth();
  const [sessionId] = useState(() => `s-${Date.now()}`);
  const [messages, setMessages] = useState([
    { role: "assistant", text: `Hi ${user?.name?.split(" ")[0] || "there"} — I'm your ITSME agent. Ask about anomalies, recommendations, or scenarios on this ${page || "console"}.`, citations: [] }
  ]);
  const [input, setInput] = useState("");
  const [fast, setFast] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "", citations: [] }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("itsme_token") || ""}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text, context_page: page, fast }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          // event: citations block
          if (p.startsWith("event: citations")) {
            const dataLine = p.split("\n").find((l) => l.startsWith("data: "));
            if (dataLine) {
              try {
                const cites = JSON.parse(dataLine.slice(6));
                setMessages((m) => {
                  const c = [...m];
                  c[c.length - 1] = { ...c[c.length - 1], citations: cites };
                  return c;
                });
              } catch (_e) {}
            }
            continue;
          }
          if (p.startsWith("data: ")) {
            const chunk = p.slice(6);
            if (chunk === "[DONE]") continue;
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + chunk };
              return c;
            });
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Failed to reach AI. Please retry.", citations: [] };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#0F1117] border-l border-[#272A35] p-0"
                    style={{ borderTop: "2px solid #00E5FF" }}>
        <SheetHeader className="p-4 border-b border-[#272A35]">
          <SheetTitle className="font-heading tracking-tight flex items-center gap-2">
            <Brain size={18} className="text-[#00E5FF]" /> Chat with AI
          </SheetTitle>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-1">
            <span>Context: <span className="text-slate-200">{page || "console"}</span></span>
            <label className="flex items-center gap-2 cursor-pointer" title="Fast mode uses Gemini Flash">
              <Lightning size={12} className={fast ? "text-[#FFB000]" : "text-slate-500"} />
              <span>Fast</span>
              <Switch data-testid="chat-fast-toggle" checked={fast} onCheckedChange={setFast} />
            </label>
          </div>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-4 h-[calc(100vh-190px)]">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[90%] rounded-md p-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#0055FF] text-white"
                  : "bg-[#13151A] border border-[#272A35] text-slate-100"
              }`}>
                {m.text || <span className="text-slate-500 italic">thinking…</span>}
                {m.role === "assistant" && m.citations && m.citations.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#272A35]" data-testid="chat-citations">
                    <div className="data-label" style={{ color: "#00E5FF" }}>Sources</div>
                    <div className="mt-2 space-y-1.5">
                      {m.citations.map((c) => (
                        <div key={c.n} className="text-[11px] text-slate-400 leading-snug">
                          <span className="text-[#00E5FF] font-mono mr-1">[{c.n}]</span>
                          <span className="text-slate-200 font-medium">{c.filename}</span>
                          <span className="text-slate-500 ml-1">· chunk {c.chunk_index}</span>
                          <div className="text-slate-500 mt-0.5 line-clamp-2">{c.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={send} className="p-3 border-t border-[#272A35] flex gap-2">
          <Input
            data-testid="chat-input"
            placeholder="Ask about anomalies, KPIs, root causes…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="bg-[#13151A] border-[#272A35]"
          />
          <Button data-testid="chat-send" type="submit" disabled={streaming}
                  className="bg-[#0055FF] hover:bg-[#0044cc]">
            <PaperPlaneTilt size={16} />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
